/**
 * Utility for extracting data from backend ApiResponse wrappers.
 * Follows standards defined in ENTERPRISE_ARCHITECTURE.md (ApiResponse / Page patterns).
 */

export interface ApiResponse<T> {
  success: boolean;
  data: T;
  message?: string;
  errorCode?: string;
}

export interface PageResponse<T> {
  content: T[];
  totalElements: number;
  totalPages: number;
  size: number;
  number: number;
}

export const dataUtils = {
  /**
   * Extracts clean array data from an ApiResponse or raw array.
   */
  extractData: <T>(response: any): T[] => {
    if (!response) return [];
    
    // Handle ApiResponse<T[]> wrapper
    const data = response.data !== undefined ? response.data : response;
    
    // Handle Spring Pageable structure
    if (data && data.content && Array.isArray(data.content)) {
      return data.content;
    }
    
    // Handle standard data array wrapper
    if (data && data.data && Array.isArray(data.data)) {
      return data.data;
    }
    
    // Return if already an array
    if (Array.isArray(data)) {
      return data;
    }
    
    // Fallback for single object response wrapped in array
    if (typeof data === 'object' && Object.keys(data).length > 0) {
      // If it's a single entity (not an array), we might wrap it depending on use case,
      // but usually getAll expects an array.
      return []; 
    }

    return [];
  },

  /**
   * Extracts a single item from an ApiResponse wrapper.
   */
  extractItem: <T>(response: any): T => {
    if (!response) return null as any;
    return response.data !== undefined ? response.data : response;
  },

  /**
   * Normalizes lookup data by mapping generic backend fields (id, name)
   * to flavored frontend properties (e.g., societyTypeId, societyTypeName).
   * This is a "Resiliency Layer" to handle naming convention mismatches between BE/FE.
   */
  normalizeLookup: <T>(response: any, idKey: string, nameKey?: string): T[] => {
    const rawData = dataUtils.extractData<any>(response);
    if (!Array.isArray(rawData)) return [];

    return rawData.map(item => dataUtils.normalizeItem<T>(item, idKey, nameKey));
  },

  /**
   * Normalizes a single item from an ApiResponse wrapper.
   */
  normalizeItem: <T>(response: any, idKey: string, nameKey?: string): T => {
    const item = dataUtils.extractItem<any>(response);
    if (!item || typeof item !== 'object') return item as T;

    const normalized = { ...item };
    
    // Map 'id' to specific flavored ID if flavored ID is missing
    if (item.id !== undefined && item[idKey] === undefined) {
      normalized[idKey] = item.id;
    }
    
    // Map 'name' to specific flavored Name if flavored Name is missing
    if (nameKey && item.name !== undefined && item[nameKey] === undefined) {
      normalized[nameKey] = item.name;
    }
    
    // Map 'description' to Name if name is missing
    if (nameKey && item.description !== undefined && item[nameKey] === undefined) {
      normalized[nameKey] = item.description;
    }

    return normalized as T;
  }
};
